import type { PartitionPack } from './partitionPack';
import { parsePartitionPack } from './partitionPack';
import type { KLV } from './klv';
import { SMPTE_UL_PARTITION_PACKS, SMPTE_UL_PRIMER_PACK, isStructuralMetadataSet, isUnknownKLVOrFill } from './mxfDictionary';
import { cloneDeep } from 'lodash';
import type { OpenResource } from '../../io/resource';

export interface PartitionPackAndHeaders {
  partitionPack: PartitionPack,
  headers: KLV[],
}

// Will fails once we encounter a non-footer partition pack so that we don't keep reading until end end of
// the file if the last frame hint was wrong
export function ensureFooterPartition(): TransformStream<PartitionPackAndHeaders, PartitionPackAndHeaders> {
  return new TransformStream<PartitionPackAndHeaders, PartitionPackAndHeaders>({
    transform: (partitionPackAndHeaders: PartitionPackAndHeaders, controller) => {
      if (partitionPackAndHeaders.partitionPack.partitionType !== 'footer') {
        controller.error(new Error('Encountered non-footer partition'));
      } else {
        controller.enqueue(partitionPackAndHeaders);
      }
    }
  });
}

export function readPartitionPackAndHeaders(): TransformStream<KLV, PartitionPackAndHeaders> {
  let partitionPack : PartitionPack | null = null;
  const headers : KLV[] = [];
  let inHeaders = false;
  return new TransformStream<KLV, PartitionPackAndHeaders>({
    transform: (klv: KLV) => {
      if (SMPTE_UL_PARTITION_PACKS.has(klv.key)) {
        if (partitionPack === null) {
          partitionPack = parsePartitionPack(klv);
          inHeaders = true;
        } else {
          // This means we've encountered the partition pack of the next partition which means
          // we've read all the headers
          inHeaders = false;
        }
      } else if (!inHeaders) {
        // Ignore all KLVs after headers
      } else if (isUnknownKLVOrFill(klv.key)) {
        if (headers.length > 0) {
          headers.push(klv);
        }
      } else if (isStructuralMetadataSet(klv.key) || (klv.key === SMPTE_UL_PRIMER_PACK)) {
        headers.push(klv);
      } else {
        inHeaders = false;
      }
    },
    flush: (controller) => {
      if (partitionPack === null) {
        throw Error('No partition pack encountered');
      }
      controller.enqueue({ partitionPack, headers });
    }
  });
}

export function deriveFooterFromHeader(partitionPackAndHeader: PartitionPackAndHeaders): PartitionPackAndHeaders {
  return {
    partitionPack: {
      ...partitionPackAndHeader.partitionPack,
      partitionType: 'footer',
      closed: true,
      complete: true,
    },
    headers: cloneDeep(partitionPackAndHeader.headers),
  };
}

export async function completeTransform<T>(
  openResource: OpenResource,
  what: string,
  convert: (readable: ReadableStream<Uint8Array>) => ReadableStream<T>
): Promise<T> {
  const reader = convert(openResource.stream).getReader();
  const result = await reader.read();
  if (result.done) {
    throw new Error(`Could not parse ${what}.`);
  }
  return result.value;
}

